import { useMutation } from "@tanstack/react-query";
import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../lib/api";

/**
 * Generic click-to-edit field. Hover reveals a pencil icon; clicking
 * swaps the rendered value for an input bound to the same value.
 * Pressing Enter commits via `onSave(value)`; Esc cancels.
 *
 * Three input modes:
 *   - `kind="text"` (default) → single-line text input
 *   - `kind="textarea"`       → multi-line textarea
 *   - `kind="select"`         → dropdown, requires `options`
 *   - `kind="boolean"`        → on/off toggle, commits on click
 *   - `kind="number"`         → numeric input
 *
 * `onSave` returns a promise. The component shows the API error
 * message inline if the promise rejects with an `ApiError`.
 */
export interface EditableFieldOption {
  value: string;
  label: string;
}

export interface EditableFieldProps {
  label: string;
  value: string | number | boolean | null;
  onSave: (newValue: string | number | boolean | null) => Promise<unknown>;
  kind?: "text" | "textarea" | "select" | "boolean" | "number";
  rtl?: boolean;
  options?: EditableFieldOption[];
  placeholder?: string;
  readOnly?: boolean;
}

function displayValue(
  value: string | number | boolean | null,
  options?: EditableFieldOption[],
): React.ReactNode {
  if (value === null || value === "") {
    return <span className="italic text-slate-400">—</span>;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (options && typeof value === "string") {
    const opt = options.find((o) => o.value === value);
    if (opt) return opt.label;
  }
  return String(value);
}

export function EditableField({
  label,
  value,
  onSave,
  kind = "text",
  rtl = false,
  options,
  placeholder,
  readOnly = false,
}: EditableFieldProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const initial = value === null ? "" : String(value);
  const [draft, setDraft] = useState<string>(initial);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (newValue: string | number | boolean | null) => onSave(newValue),
    onSuccess: () => {
      setEditing(false);
      setErrorMsg(null);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else if (e instanceof Error) setErrorMsg(e.message);
      else setErrorMsg(String(e));
    },
  });

  const startEdit = () => {
    if (readOnly) return;
    setDraft(value === null ? "" : String(value));
    setErrorMsg(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setErrorMsg(null);
  };
  const save = () => {
    const trimmed = draft.trim();
    if (kind === "number") {
      if (trimmed === "" && value === null) return cancel();
      const n = trimmed === "" ? null : Number(trimmed);
      if (n !== null && Number.isNaN(n)) {
        setErrorMsg(t("app.ef_not_a_number"));
        return;
      }
      if (n === value) return cancel();
      mutation.mutate(n);
      return;
    }
    const stringified = trimmed === "" ? null : trimmed;
    if (stringified === (value === null ? null : String(value))) return cancel();
    mutation.mutate(stringified);
  };

  // Boolean toggle commits immediately on click — no edit mode.
  if (kind === "boolean") {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {label}
        </span>
        <button
          type="button"
          disabled={readOnly || mutation.isPending}
          onClick={() => mutation.mutate(!value)}
          className={`inline-flex w-fit items-center gap-1.5 rounded px-2 py-0.5 text-sm ${
            value
              ? "bg-emerald-50 text-emerald-700"
              : "bg-slate-100 text-slate-600"
          } hover:opacity-80 disabled:opacity-50`}
        >
          {value ? t("common.yes") : t("common.no")}
        </button>
        {errorMsg && (
          <p className="text-xs text-rose-700">{errorMsg}</p>
        )}
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {label}
        </span>
        <button
          type="button"
          onClick={startEdit}
          disabled={readOnly}
          className="group flex items-center gap-1.5 rounded text-start text-sm text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <span dir={rtl ? "rtl" : "ltr"} className="flex-1">
            {displayValue(value, options)}
          </span>
          {!readOnly && (
            <Pencil className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <div className="flex items-start gap-1">
        {kind === "textarea" ? (
          <textarea
            autoFocus
            dir={rtl ? "rtl" : "ltr"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
            }}
            placeholder={placeholder}
            rows={2}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        ) : kind === "select" && options ? (
          <select
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
            }}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">— None —</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            type={kind === "number" ? "number" : "text"}
            dir={rtl ? "rtl" : "ltr"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") cancel();
            }}
            placeholder={placeholder}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        )}
        <button
          type="button"
          onClick={save}
          disabled={mutation.isPending}
          className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          aria-label={t("app.ef_save_aria")}
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={cancel}
          className="rounded p-1 text-slate-500 hover:bg-slate-100"
          aria-label={t("app.ef_cancel_aria")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {errorMsg && <p className="text-xs text-rose-700">{errorMsg}</p>}
    </div>
  );
}
